import React, { useState } from 'react';
import axios from 'axios';
import {
    Building2, Users, FileText, X, Upload, Download,
    FileUp, AlertCircle, AlertTriangle, CheckCircle
} from 'lucide-react';
import { API_BASE } from './helpers';

function ImportTool() {
  const [step, setStep] = useState('select');   // select | upload | map | importing | results
  const [entityType, setEntityType] = useState('');
  const [csvData, setCsvData] = useState(null);  // { headers, preview, allRows, fileName, totalRows }
  const [fieldDefs, setFieldDefs] = useState([]);
  const [mapping, setMapping] = useState({});    // { crmField: csvColumn }
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const entityLabels = {
    contact: 'Contacts',
    property: 'Properties',
    contract: 'Contracts',
  };

  const entityDuplicateInfo = {
    contact: 'Duplicates detected by scode, or by email if no scode.',
    property: 'Duplicates detected by Yardi Code.',
    contract: 'Duplicates detected by contract number. Contracts require a matching contact email to link.',
  };

  // Load field definitions when entity type is selected
  const selectEntity = async (type) => {
    setEntityType(type);
    setError(null);
    try {
      const res = await axios.get(`${API_BASE}/import/fields/${type}`);
      setFieldDefs(res.data);
      setStep('upload');
    } catch (err) {
      setError('Failed to load field definitions');
    }
  };

  // Handle file upload
  const handleFile = async (file) => {
    if (!file) return;
    setError(null);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(`${API_BASE}/import/parse`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setCsvData(res.data);
      // Auto-map: try to match CSV headers to CRM fields by similarity
      const autoMap = {};
      fieldDefs.forEach(fd => {
        const match = res.data.headers.find(h => {
          const hLower = h.toLowerCase().replace(/[_\s-]/g, '');
          const fLower = fd.field.toLowerCase();
          const lLower = fd.label.toLowerCase().replace(/[_\s-]/g, '');
          return hLower === fLower || hLower === lLower || h.toLowerCase() === fd.label.toLowerCase();
        });
        if (match) autoMap[fd.field] = match;
      });
      setMapping(autoMap);
      setStep('map');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to parse CSV file');
    }
  };

  const handleFileInput = (e) => {
    handleFile(e.target.files[0]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  // Update mapping
  const updateMapping = (crmField, csvColumn) => {
    setMapping(prev => {
      const next = { ...prev };
      if (csvColumn === '') {
        delete next[crmField];
      } else {
        next[crmField] = csvColumn;
      }
      return next;
    });
  };

  // Validate mapping — check required fields
  const getMappingErrors = () => {
    const errs = [];
    fieldDefs.forEach(fd => {
      if (fd.required && !mapping[fd.field]) {
        errs.push(`"${fd.label}" is required but not mapped`);
      }
    });
    return errs;
  };

  // Run import
  const runImport = async () => {
    setImporting(true);
    setStep('importing');
    setProgress(0);
    setError(null);

    try {
      // Simulate progress (the actual request is one batch)
      const progressTimer = setInterval(() => {
        setProgress(prev => Math.min(prev + 5, 90));
      }, 200);

      const res = await axios.post(`${API_BASE}/import/${entityType}s`, {
        rows: csvData.allRows,
        mapping,
      });

      clearInterval(progressTimer);
      setProgress(100);
      setResults(res.data);
      setStep('results');
    } catch (err) {
      setError(err.response?.data?.error || 'Import failed');
      setStep('map');
    } finally {
      setImporting(false);
    }
  };

  // Reset everything
  const reset = () => {
    setStep('select');
    setEntityType('');
    setCsvData(null);
    setFieldDefs([]);
    setMapping({});
    setResults(null);
    setError(null);
    setProgress(0);
  };

  // Download sample CSV template
  const downloadTemplate = () => {
    const headers = fieldDefs.map(fd => fd.label).join(',');
    const blob = new Blob([headers + '\n'], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${entityType}_import_template.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="crm-import">
      <div className="crm-card">
        <div className="crm-card__header">
          <h2 className="crm-section__title" style={{ margin: 0 }}>
            <Upload size={22} />
            CSV Import Tool
          </h2>
          {step !== 'select' && step !== 'importing' && (
            <button className="crm-btn crm-btn--secondary" onClick={reset}>
              <X size={16} /> Start Over
            </button>
          )}
        </div>

        {/* Step indicator */}
        <div className="crm-import__steps">
          {['Select Type', 'Upload CSV', 'Map Columns', 'Import'].map((label, idx) => {
            const stepNames = ['select', 'upload', 'map', 'importing'];
            const currentIdx = stepNames.indexOf(step === 'results' ? 'importing' : step);
            return (
              <div key={label} className={`crm-import__step ${idx <= currentIdx ? 'crm-import__step--active' : ''} ${idx < currentIdx ? 'crm-import__step--done' : ''}`}>
                <div className="crm-import__step-num">{idx < currentIdx ? '✓' : idx + 1}</div>
                <span className="crm-import__step-label">{label}</span>
              </div>
            );
          })}
        </div>

        {error && (
          <div className="crm-error" style={{ marginBottom: '1rem' }}>
            <AlertCircle size={18} /> {error}
          </div>
        )}

        {/* Step 1: Select entity type */}
        {step === 'select' && (
          <div className="crm-import__select-grid">
            {Object.entries(entityLabels).map(([key, label]) => (
              <button key={key} className="crm-import__entity-btn" onClick={() => selectEntity(key)}>
                <div className="crm-import__entity-icon">
                  {key === 'contact' && <Users size={32} />}
                  {key === 'property' && <Building2 size={32} />}
                  {key === 'contract' && <FileText size={32} />}
                </div>
                <span className="crm-import__entity-label">{label}</span>
                <span className="crm-import__entity-hint">{entityDuplicateInfo[key]}</span>
              </button>
            ))}
          </div>
        )}

        {/* Step 2: Upload CSV */}
        {step === 'upload' && (
          <div>
            <div className="crm-import__upload-header">
              <h3>Upload {entityLabels[entityType]} CSV</h3>
              <button className="crm-btn crm-btn--secondary" onClick={downloadTemplate}>
                <Download size={16} /> Download Template
              </button>
            </div>
            <div
              className={`crm-import__dropzone ${dragOver ? 'crm-import__dropzone--active' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => document.getElementById('csv-file-input').click()}
            >
              <FileUp size={48} className="crm-import__dropzone-icon" />
              <p className="crm-import__dropzone-text">Drag & drop your CSV file here</p>
              <p className="crm-import__dropzone-hint">or click to browse</p>
              <input
                id="csv-file-input"
                type="file"
                accept=".csv"
                onChange={handleFileInput}
                style={{ display: 'none' }}
              />
            </div>
          </div>
        )}

        {/* Step 3: Map columns */}
        {step === 'map' && csvData && (
          <div>
            <div className="crm-import__map-header">
              <div>
                <h3>Map Columns — {csvData.fileName}</h3>
                <p className="crm-text--muted">{csvData.totalRows} rows found. Map your CSV columns to CRM fields below.</p>
              </div>
            </div>

            <div className="crm-import__map-table-wrap">
              <table className="crm-import__map-table">
                <thead>
                  <tr>
                    <th>CRM Field</th>
                    <th>CSV Column</th>
                    <th>Preview (Row 1)</th>
                  </tr>
                </thead>
                <tbody>
                  {fieldDefs.map(fd => (
                    <tr key={fd.field} className={fd.required && !mapping[fd.field] ? 'crm-import__map-row--missing' : ''}>
                      <td>
                        <span className="crm-import__field-name">
                          {fd.label}
                          {fd.required && <span className="crm-import__required">*</span>}
                        </span>
                      </td>
                      <td>
                        <select
                          className="crm-form__input crm-import__map-select"
                          value={mapping[fd.field] || ''}
                          onChange={(e) => updateMapping(fd.field, e.target.value)}
                        >
                          <option value="">— Not mapped —</option>
                          {csvData.headers.map(h => (
                            <option key={h} value={h}>{h}</option>
                          ))}
                        </select>
                      </td>
                      <td className="crm-import__preview-cell">
                        {mapping[fd.field] && csvData.preview[0] ? csvData.preview[0][mapping[fd.field]] || '—' : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Data preview */}
            <div className="crm-import__preview-section">
              <h4>Data Preview (first {Math.min(5, csvData.preview.length)} rows)</h4>
              <div className="crm-import__preview-table-wrap">
                <table className="crm-import__preview-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      {csvData.headers.map(h => <th key={h}>{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {csvData.preview.map((row, idx) => (
                      <tr key={idx}>
                        <td>{idx + 1}</td>
                        {csvData.headers.map(h => <td key={h}>{row[h] || ''}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {getMappingErrors().length > 0 && (
              <div className="crm-import__validation">
                <AlertTriangle size={16} />
                <span>Required fields not mapped: {getMappingErrors().join(', ')}</span>
              </div>
            )}

            <div className="crm-import__actions">
              <button className="crm-btn crm-btn--secondary" onClick={() => { setCsvData(null); setStep('upload'); }}>
                Back
              </button>
              <button
                className="crm-btn crm-btn--primary"
                disabled={getMappingErrors().length > 0}
                onClick={runImport}
              >
                <Upload size={16} />
                Import {csvData.totalRows} {entityLabels[entityType]}
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Importing progress */}
        {step === 'importing' && (
          <div className="crm-import__progress">
            <div className="crm-loading__spinner" />
            <h3>Importing {entityLabels[entityType]}...</h3>
            <div className="crm-import__progress-bar">
              <div className="crm-import__progress-fill" style={{ width: `${progress}%` }} />
            </div>
            <p className="crm-text--muted">{progress}% complete</p>
          </div>
        )}

        {/* Step 5: Results */}
        {step === 'results' && results && (
          <div className="crm-import__results">
            <div className="crm-import__results-icon">
              <CheckCircle size={48} />
            </div>
            <h3>Import Complete</h3>

            <div className="crm-import__results-grid">
              <div className="crm-import__results-stat crm-import__results-stat--total">
                <span className="crm-import__results-num">{results.total}</span>
                <span className="crm-import__results-label">Total Rows</span>
              </div>
              <div className="crm-import__results-stat crm-import__results-stat--success">
                <span className="crm-import__results-num">{results.imported}</span>
                <span className="crm-import__results-label">Imported</span>
              </div>
              <div className="crm-import__results-stat crm-import__results-stat--skipped">
                <span className="crm-import__results-num">{results.skipped}</span>
                <span className="crm-import__results-label">Skipped (Duplicates)</span>
              </div>
              {results.errors.length > 0 && (
                <div className="crm-import__results-stat crm-import__results-stat--errors">
                  <span className="crm-import__results-num">{results.errors.length}</span>
                  <span className="crm-import__results-label">Errors</span>
                </div>
              )}
            </div>

            {results.errors.length > 0 && (
              <div className="crm-import__errors-list">
                <h4>Errors</h4>
                {results.errors.slice(0, 20).map((e, idx) => (
                  <div key={idx} className="crm-import__error-item">
                    <AlertTriangle size={14} />
                    <span>Row {e.row}: {e.error}</span>
                  </div>
                ))}
                {results.errors.length > 20 && (
                  <p className="crm-text--muted">...and {results.errors.length - 20} more</p>
                )}
              </div>
            )}

            <div className="crm-import__actions">
              <button className="crm-btn crm-btn--primary" onClick={reset}>
                Import More Data
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Admin Placeholder View

export default ImportTool;
